import React, { FC, ReactNode, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContrastIcon from '@mui/icons-material/Contrast';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useMutation } from '@apollo/client';
import { formatDate } from '../Formatters';
import { SET_CHECK_ITEM_STATE, SET_TASK_STATE } from './revisions.graphql';
import { Revision } from './Details';
import { Unpacked } from '../graphQLTypes/types';
import { ChecklistCheckItemState, ChecklistTaskState } from '../__generated__/graphql';

type Milestones = NonNullable<NonNullable<Revision>['checklist']>['milestones'];
type Milestone = NonNullable<Unpacked<NonNullable<Milestones>>>;
type CheckItems = NonNullable<NonNullable<Revision>['checklist']>['checkItems'];
type CheckItem = NonNullable<Unpacked<NonNullable<CheckItems>>>;
type ChecklistTask = Unpacked<NonNullable<NonNullable<Unpacked<NonNullable<NonNullable<Revision>['checklist']>['milestones']>>['tasks']>>;
type Task = NonNullable<ChecklistTask>;

export const stateLabels: Record<ChecklistTaskState, string> = {
  [ChecklistTaskState.NotStarted]: 'Ikke startet',
  [ChecklistTaskState.Started]: 'Påbegynt',
  [ChecklistTaskState.Done]: 'Ferdig',
  [ChecklistTaskState.NotRelevant]: 'Ikke relevant',
};

const stateIcons: Record<ChecklistTaskState, ReactNode> = {
  [ChecklistTaskState.NotStarted]: <RadioButtonUncheckedIcon color="action" />,
  [ChecklistTaskState.Started]: <ContrastIcon color="warning" />,
  [ChecklistTaskState.Done]: <CheckCircleIcon color="primary" />,
  [ChecklistTaskState.NotRelevant]: <RadioButtonUncheckedIcon color="disabled" />,
};

const nextState: Partial<Record<ChecklistTaskState, ChecklistTaskState>> = {
  [ChecklistTaskState.NotStarted]: ChecklistTaskState.Started,
  [ChecklistTaskState.Started]: ChecklistTaskState.Done,
  [ChecklistTaskState.Done]: ChecklistTaskState.NotStarted,
};

const nextStep: Partial<Record<ChecklistTaskState, string>> = {
  [ChecklistTaskState.NotStarted]: 'klikk for å markere som påbegynt',
  [ChecklistTaskState.Started]: 'klikk for å markere som ferdig',
  [ChecklistTaskState.Done]: 'klikk for å sette tilbake til ikke startet',
};

export const CheckItemList: FC<{ revisionId: string; items: CheckItems; history: boolean }> = ({ revisionId, items, history }) => (
  <List dense disablePadding sx={{ mt: 1 }}>
    {(items ?? [])
      .filter((x): x is CheckItem => !!x)
      .map((item) => (
        <CheckItemRow key={item.id} revisionId={revisionId} history={history} item={item} />
      ))}
  </List>
);

export const ChecklistPhases: FC<{ revisionId: string; milestones: Milestones; history: boolean }> = ({ revisionId, milestones, history }) => {
  const numbered = [...(milestones ?? [])]
    .filter((x): x is Milestone => !!x)
    .sort((a, b) => a.number - b.number)
    .map((milestone, index) => ({ ...milestone, number: index + 1 }));

  return (
    <>
      {numbered.map((milestone) => (
        <List
          key={milestone.name}
          dense
          disablePadding
          subheader={
            <ListSubheader sx={{ backgroundColor: 'rgba(255,255,255,0.9)', lineHeight: '32px', mt: 1 }}>
              {milestone.number} - {milestone.name} ({milestone.completedTasks}/{milestone.numberOfTasks})
            </ListSubheader>
          }>
          {milestone.tasks
            ?.filter((x): x is Task => !!x)
            .map((task) => (
              <ActivityItem key={task.id} revisionId={revisionId} history={history} task={task} />
            ))}
        </List>
      ))}
    </>
  );
};

const ActivityItem: FC<{ revisionId: string; task: Task; history: boolean }> = ({ revisionId, task, history }) => {
  const { state, setState, dialogs } = useTaskState(revisionId, task);
  const notRelevant = state === ChecklistTaskState.NotRelevant;
  const next = nextState[state];

  return (
    <ListItem sx={{ alignItems: 'flex-start', py: 0.25 }}>
      <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
        {history || !next ? (
          <Box sx={{ display: 'flex', p: 1 }} aria-label={stateLabels[state]}>
            {stateIcons[state]}
          </Box>
        ) : (
          <Tooltip title={`${stateLabels[state]} – ${nextStep[state]}`}>
            <IconButton aria-label={`${task.name}: ${stateLabels[state]} – ${nextStep[state]}`} onClick={() => setState(next)}>
              {stateIcons[state]}
            </IconButton>
          </Tooltip>
        )}
      </ListItemIcon>
      <ListItemText
        sx={{ my: 1 }}
        primary={
          <Typography variant="body2" color={notRelevant ? 'textSecondary' : 'initial'}>
            {task.name}
          </Typography>
        }
        secondary={<TaskHistory task={task} />}
      />
      {(task.allowNotRelevant || notRelevant) && (
        <NotRelevantToggle
          checked={notRelevant}
          history={history}
          onChange={() => setState(notRelevant ? ChecklistTaskState.NotStarted : ChecklistTaskState.NotRelevant)}
        />
      )}
      {dialogs}
    </ListItem>
  );
};

const NotRelevantToggle: FC<{ checked: boolean; history: boolean; onChange: () => void }> = ({ checked, history, onChange }) => (
  <FormControlLabel
    sx={{ flexShrink: 0, ml: 1, mr: 0, my: 0.5 }}
    control={
      history ? (
        checked ? (
          <CheckBoxIcon color="disabled" fontSize="small" />
        ) : (
          <CropSquareIcon color="disabled" fontSize="small" />
        )
      ) : (
        <Checkbox size="small" sx={{ p: 0.5 }} onChange={onChange} checked={checked} color="primary" />
      )
    }
    label={
      <Typography variant="body2" color="textSecondary" noWrap>
        Ikke relevant
      </Typography>
    }
  />
);

const CheckItemRow: FC<{ revisionId: string; item: CheckItem; history: boolean }> = ({ revisionId, item, history }) => {
  const { state, setState, dialogs } = useCheckItemState(revisionId, item);
  const done = state === ChecklistCheckItemState.Checked;
  const notRelevant = state === ChecklistCheckItemState.NotRelevant;

  return (
    <ListItem sx={{ alignItems: 'flex-start', py: 0.25 }}>
      <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
        {history ? (
          notRelevant ? (
            <NotInterestedIcon color="disabled" fontSize="small" />
          ) : (
            <CheckBoxIcon color="disabled" fontSize="small" />
          )
        ) : (
          <Checkbox
            color="primary"
            size="small"
            sx={{ p: 0.5 }}
            onChange={() => setState(done ? ChecklistCheckItemState.Open : ChecklistCheckItemState.Checked)}
            checked={done}
            indeterminate={notRelevant}
            disabled={notRelevant}
          />
        )}
      </ListItemIcon>
      <ListItemText
        sx={{ mt: 0.5, mb: 0 }}
        primary={
          <Typography variant="body2" color={notRelevant ? 'textSecondary' : 'initial'}>
            {item.name}
          </Typography>
        }
        secondary={<History at={item.checkedAt} by={item.checkedBy?.name} comment={item.comment} />}
      />
      {item.allowNotRelevant && (
        <NotRelevantToggle
          checked={notRelevant}
          history={history}
          onChange={() => setState(notRelevant ? ChecklistCheckItemState.Open : ChecklistCheckItemState.NotRelevant)}
        />
      )}
      {dialogs}
    </ListItem>
  );
};

const TaskHistory: FC<{ task: Task }> = ({ task }) =>
  task.completed ? (
    <History at={task.completedAt} by={task.completedBy?.name} comment={task.comment} />
  ) : (
    <History at={task.startedAt} by={task.startedBy?.name} prefix="Påbegynt " comment={task.comment} />
  );

const History: FC<{ at: unknown; by: string | null | undefined; prefix?: string; comment: string | null | undefined }> = ({ at, by, prefix = '', comment }) => (
  <>
    {!!at && `${prefix}${formatDate(at as string)} - ${by}`}
    {comment && (
      <Typography component="span" variant="body2" color="textSecondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
        {comment}
      </Typography>
    )}
  </>
);

function useTaskState(revisionId: string, task: Task) {
  const [setTaskState] = useMutation(SET_TASK_STATE);

  return useStateChange(task.state, task.name ?? '', ChecklistTaskState.NotRelevant, !!task.requireCommentWhenNotRelevant, async (state, comment) => {
    const result = await setTaskState({ variables: { input: { id: revisionId, taskId: task.id, state, comment } } });
    return result.data?.revisionSetTaskState?.errors?.[0];
  });
}

function useCheckItemState(revisionId: string, item: CheckItem) {
  const [setCheckItemState] = useMutation(SET_CHECK_ITEM_STATE);

  return useStateChange(item.state, item.name ?? '', ChecklistCheckItemState.NotRelevant, !!item.requireCommentWhenNotRelevant, async (state, comment) => {
    const result = await setCheckItemState({ variables: { input: { id: revisionId, itemId: item.id, state, comment } } });
    return result.data?.revisionSetCheckItemState?.errors?.[0];
  });
}

function useStateChange<S extends string>(
  current: S,
  name: string,
  notRelevant: S,
  requireReason: boolean,
  save: (state: S, comment?: string) => Promise<{ message?: string } | object | null | undefined>,
) {
  const [pending, setPending] = useState<S | null>(null);
  const [askingForReason, setAskingForReason] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = async (state: S, comment?: string) => {
    setPending(state);
    try {
      const failure = await save(state, comment);
      if (failure) setError('message' in failure && failure.message ? failure.message : 'Kunne ikke oppdatere punktet.');
    } finally {
      setPending(null);
    }
  };

  const setState = (state: S) => {
    if (state === notRelevant && requireReason) {
      setAskingForReason(true);
      return;
    }

    change(state);
  };

  const dialogs = (
    <>
      {askingForReason && (
        <ReasonDialog
          taskName={name}
          onCancel={() => setAskingForReason(false)}
          onGiven={(comment) => {
            setAskingForReason(false);
            change(notRelevant, comment);
          }}
        />
      )}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );

  return { state: pending ?? current, setState, dialogs };
}

const ReasonDialog: FC<{ taskName: string; onCancel: () => void; onGiven: (comment: string) => void }> = ({ taskName, onCancel, onGiven }) => {
  const [comment, setComment] = useState('');

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Ikke relevant</DialogTitle>
      <DialogContent>
        <DialogContentText>«{taskName}» krever en begrunnelse for å kunne merkes som ikke relevant.</DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          placeholder="Hvorfor er punktet ikke relevant?"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onCancel}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!comment.trim()} onClick={() => onGiven(comment)}>
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
};
